import { useUserProfileInfo } from '@/entities/user';
import {
  Button,
  Input,
  Loader,
  ModalComponentWrapper,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import PencilIcon from '@/shared/assets/edit-icon.svg?react';
import { useConfirmationModal } from '@/widgets/confirmation-modal';
import { useContactUs } from '@/shared/lib';

type Props = {
  onBack: () => void;
  ewalletAccNum: string | undefined;
};

export function UserProfileInfoModal({ onBack, ewalletAccNum }: Props) {
  const { t } = useTranslation();

  const { userProfileInfo, isUserProfileInfoLoading, userProfileInfoError } =
    useUserProfileInfo();
  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();

  const { links } = useContactUs({ ewalletAccNum });

  if (isUserProfileInfoLoading || !userProfileInfo) {
    return <Loader />;
  }

  if (userProfileInfoError) {
    return <div>Error</div>;
  }

  const handleDeleteAccountClick = () => {
    openModal({
      title: t('confirmationModal.deleteAccount.title'),
      description: t('confirmationModal.deleteAccount.description'),
      confirmText: t('confirmationModal.deleteAccount.confirmText'),
      cancelText: t('confirmationModal.deleteAccount.cancelText'),
      onConfirm: () => {
        window.open(
          links?.deleteAccountRequest || links?.defaultRequest || '',
          '_blank'
        );
      },
    });
  };

  const handleEditAccountClick = () => {
    openModal({
      title: t('confirmationModal.editAccount.title'),
      description: t('confirmationModal.editAccount.description'),
      confirmText: t('confirmationModal.editAccount.confirmText'),
      cancelText: t('confirmationModal.editAccount.cancelText'),
      onConfirm: () => {
        window.open(
          links?.changeAccountInfoRequest || links?.defaultRequest || '',
          '_blank'
        );
      },
    });
  };

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.accountInfo.title')}
      onBack={onBack}
      actions={
        <Button
          variant="secondary"
          onClick={handleEditAccountClick}
          className="flex items-center justify-center w-[4.2rem] h-[4.2rem] bg-muted hover:bg-muted/80 bg-transparent-bg transition-colors text-foreground shadow-none self-start rounded-full border border-border mr-[0.8rem]"
          aria-label="edit">
          <PencilIcon className="size-9 w-[2.4rem] h-[2.4rem] text-foreground" />
        </Button>
      }>
      <div className="flex flex-col gap-[1.6rem]">
        {userProfileInfo.userData.map(({ paramId, paramName, paramValue }) => (
          <Input
            key={paramId}
            value={paramValue}
            type="text"
            disabled
            label={paramName}
            className="p3"
          />
        ))}

        <Button
          type="submit"
          variant="ghost"
          size="lg"
          className="w-full text-destructive"
          onClick={handleDeleteAccountClick}>
          {t('personalAccount.userProfile.accountInfo.deleteAccountButtonText')}
        </Button>
      </div>

      {ConfirmationModal}
    </ModalComponentWrapper>
  );
}
